// Planting Calendar (web) — month-by-month sow / plant-out / harvest for the
// grower's climate zone. Photo-backed HUD. Mirrors the mobile app/calendar.tsx
// and consumes the shared GET /api/planner/calendar.
import React, { useEffect, useMemo, useState } from 'react';
import Layout from '@/components/Layout';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Snowflake, Sprout, Shovel, Scissors, ChevronRight, Leaf } from 'lucide-react';
import { cropImage } from '@/lib/cropImage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const C = {
  sage: '#E5EBDD', cream: '#FAF6EC', forest: '#1A4331', forestDeep: '#0F2D1F',
  leaf: '#6B9F4A', leafDeep: '#4E7A33', gold: '#C9A85B', terracotta: '#C66B4A', lime: '#B4D330', sky: '#5E86AE',
  text: '#2A3528', muted: '#5C665C', white: '#FFFFFF',
};
const CARD_SH = '0 6px 16px -2px rgba(26,67,49,0.12)';
const V = {
  h1: { fontFamily: 'Outfit, sans-serif', fontWeight: 700, fontSize: 32, lineHeight: '38px', color: C.forest },
  h3: { fontFamily: 'Outfit, sans-serif', fontWeight: 600, fontSize: 20, lineHeight: '26px', color: C.forest },
  title: { fontFamily: 'Outfit, sans-serif', fontWeight: 600, fontSize: 17, lineHeight: '23px', color: C.forest },
  small: { fontFamily: 'Manrope, sans-serif', fontWeight: 400, fontSize: 13, lineHeight: '19px', color: C.muted },
  label: { fontFamily: 'Manrope, sans-serif', fontWeight: 600, fontSize: 13, lineHeight: '18px', color: C.muted },
};

const SECTIONS = [
  { key: 'sow', title: 'Sow seeds', field: 'sowing_months', Icon: Sprout, tone: C.leafDeep, empty: 'No direct sowing recommended this month.' },
  { key: 'plant', title: 'Plant out seedlings', field: 'transplant_months', Icon: Shovel, tone: C.sky, empty: 'Nothing to transplant this month.' },
  { key: 'harvest', title: 'Harvest', field: 'harvest_months', Icon: Scissors, tone: C.gold, empty: 'No harvests expected this month.' },
];

function PlantingCalendar({ user }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [monthIdx, setMonthIdx] = useState(new Date().getMonth());

  useEffect(() => {
    axios.get(`${API}/planner/calendar`, { withCredentials: true })
      .then((r) => {
        setData(r.data);
        if (r.data?.current_month_num) setMonthIdx(r.data.current_month_num - 1);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const months = data?.months || [];
  const monthName = months[monthIdx] || '';
  const crops = useMemo(() => data?.crops || [], [data]);
  const frost = data?.frost;
  const frostMonths = frost?.frost_months || [];
  const isFrostMonth = frostMonths.includes(monthName);

  // Next upcoming month (wrapping) that has any crop for a given field — kills dead "no crops" states.
  const nextWindow = (field) => {
    for (let step = 1; step <= 12; step++) {
      const i = (monthIdx + step) % 12;
      const m = months[i];
      if (crops.some((c) => (c[field] || []).includes(m))) return m;
    }
    return null;
  };

  const sowCount = crops.filter((c) => (c.sowing_months || []).includes(monthName)).length;
  const heroCrop = crops.find((c) => (c.sowing_months || []).includes(monthName));
  const heroImg = cropImage(heroCrop ? heroCrop.name : 'harvest', heroCrop?.category);

  return (
    <Layout user={user}>
      <div className="space-y-6" data-testid="planting-calendar">
        <div>
          <h1 style={V.h1}>Planting Calendar</h1>
          <p style={V.small} className="mt-1 flex items-center gap-1">
            <Leaf className="w-3.5 h-3.5" style={{ color: C.leaf }} />
            {data?.zone_label ? `${data.zone_label} · what to do each month` : 'Sow, plant out & harvest by month'}
          </p>
        </div>

        {loading ? (
          <div className="grid gap-4">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-40 rounded-[22px] animate-pulse" style={{ background: 'rgba(255,255,255,0.6)' }} />)}</div>
        ) : (
          <>
            {/* Photo HUD hero */}
            <div className="relative overflow-hidden rounded-[24px]" style={{ boxShadow: CARD_SH }} data-testid="calendar-hero">
              <img src={heroImg} alt="" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0" style={{ background: 'linear-gradient(145deg, rgba(15,45,31,0.78), rgba(15,45,31,0.5) 55%, rgba(26,67,49,0.92))' }} />
              <div className="relative p-6 sm:p-8">
                <p className="text-[11px] font-bold tracking-[0.25em] uppercase" style={{ color: C.lime }}>In your garden</p>
                <h2 className="text-3xl sm:text-4xl font-bold mt-1" style={{ fontFamily: 'Outfit, sans-serif', color: C.cream }} data-testid="calendar-hero-month">{monthName}</h2>
                <p className="mt-1 text-sm" style={{ color: 'rgba(250,246,236,0.85)' }}>
                  {sowCount > 0
                    ? `${sowCount} crop${sowCount === 1 ? '' : 's'} you can sow now`
                    : `Nothing to direct-sow this month${nextWindow('sowing_months') ? ` — next window opens in ${nextWindow('sowing_months')}` : ''}`}
                </p>
                {isFrostMonth && (
                  <div className="mt-4 inline-flex items-start gap-2 rounded-2xl px-3.5 py-2.5" style={{ background: 'rgba(94,134,174,0.28)', border: '1px solid rgba(180,206,232,0.4)' }} data-testid="calendar-frost-banner">
                    <Snowflake className="w-4 h-4 mt-0.5" style={{ color: '#CFE0F2' }} />
                    <span className="text-xs" style={{ color: '#EAF2FB' }}>
                      Frost risk in {monthName}. Start tender crops undercover, or wait until after the last frost{frost?.last_frost ? ` in ${frost.last_frost}` : ''}.
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Month selector */}
            <div className="flex gap-2 overflow-x-auto hg-scroll pb-1 -mx-1 px-1" data-testid="calendar-months">
              {months.map((m, i) => {
                const active = i === monthIdx;
                const frostM = frostMonths.includes(m);
                return (
                  <button
                    key={m}
                    onClick={() => setMonthIdx(i)}
                    data-testid={`calendar-month-${m.slice(0, 3)}`}
                    className="flex items-center gap-1 flex-shrink-0 rounded-full px-4 h-10 text-sm font-semibold transition-colors"
                    style={active
                      ? { background: C.forest, color: C.cream }
                      : { background: C.cream, color: C.forest, border: `1px solid rgba(26,67,49,0.14)` }}
                  >
                    {frostM && <Snowflake className="w-3 h-3" style={{ color: active ? '#CFE0F2' : C.sky }} />}
                    {m.slice(0, 3)}
                  </button>
                );
              })}
            </div>

            {/* Sections */}
            {SECTIONS.map((s) => {
              const list = crops.filter((c) => (c[s.field] || []).includes(monthName));
              const next = list.length === 0 ? nextWindow(s.field) : null;
              return (
                <div key={s.key} data-testid={`calendar-section-${s.key}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <span className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: `${s.tone}1f` }}><s.Icon className="w-5 h-5" style={{ color: s.tone }} /></span>
                    <h3 style={V.title} className="flex-1">{s.title}</h3>
                    <span className="rounded-full text-xs font-bold px-2.5 py-1" style={{ background: 'rgba(180,211,48,0.22)', color: C.forest }} data-testid={`calendar-count-${s.key}`}>{list.length}</span>
                  </div>
                  {list.length === 0 ? (
                    <div className="rounded-2xl px-4 py-5 flex items-center gap-2" style={{ background: C.cream }}>
                      <span style={V.small}>{s.empty}</span>
                      {next && (
                        <button onClick={() => setMonthIdx(months.indexOf(next))} className="inline-flex items-center gap-1 text-sm font-semibold ml-auto" style={{ color: C.leafDeep }} data-testid={`calendar-next-${s.key}`}>
                          Next in {next} <ChevronRight className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                      {list.map((c) => {
                        const tender = isFrostMonth && c.frost_tender && s.key !== 'harvest';
                        return (
                          <Link key={c.crop_id} to={`/crops/${c.crop_id}`} data-testid={`calendar-crop-${c.crop_id}`} className="relative overflow-hidden rounded-2xl group" style={{ boxShadow: CARD_SH, aspectRatio: '3/2' }}>
                            <img src={cropImage(c.name, c.category)} alt={c.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                            <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(15,45,31,0.05), rgba(15,45,31,0.4), rgba(15,45,31,0.9))' }} />
                            {tender && (
                              <span className="absolute top-2 right-2 inline-flex items-center gap-1 rounded-full px-2 py-0.5" style={{ background: 'rgba(198,107,74,0.92)' }}>
                                <Snowflake className="w-3 h-3" style={{ color: C.cream }} />
                              </span>
                            )}
                            <div className="absolute bottom-0 left-0 right-0 p-3">
                              <p className="font-semibold text-sm leading-tight" style={{ color: C.cream, fontFamily: 'Outfit, sans-serif' }}>{c.name}</p>
                              {c.weeks_to_maturity ? <p className="text-[11px] mt-0.5" style={{ color: 'rgba(250,246,236,0.8)' }}>~{c.weeks_to_maturity} weeks</p> : null}
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </>
        )}
      </div>
    </Layout>
  );
}

export default PlantingCalendar;
